import React, { useState, useMemo, useEffect } from 'react';
import { Layout, Menu, Avatar, Dropdown, Typography, Image, Drawer, Grid, Badge, Popover, Button, Empty, Divider, Tag, Spin } from 'antd';
import {
    DashboardOutlined,
    UserOutlined,
    TeamOutlined,
    CalendarOutlined,
    MedicineBoxOutlined,
    FileTextOutlined,
    DollarOutlined,
    SettingOutlined,
    LogoutOutlined,
    MenuFoldOutlined,
    MenuUnfoldOutlined,
    BarChartOutlined,
    BankOutlined,
    ShoppingOutlined,
    ExperimentOutlined,
    CameraOutlined,
    ShoppingCartOutlined,
    AppstoreOutlined,
    ImportOutlined,
    UnorderedListOutlined,
    RollbackOutlined,
    QuestionCircleOutlined,
    BellOutlined,
    CheckOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useApp } from '../../context/AppContext';
import { notificationService } from '../../services';
import LanguageSwitcher from '../LanguageSwitcher/LanguageSwitcher';
import SystemGuideModal from '../SystemGuide/SystemGuideModal';
import dayjs from 'dayjs';
import './MainLayout.css';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;
const { useBreakpoint } = Grid;

const MainLayout = ({ children }) => {
    const [collapsed, setCollapsed] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [guideOpen, setGuideOpen] = useState(false);
    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [notificationLoading, setNotificationLoading] = useState(false);
    const [notificationPopoverOpen, setNotificationPopoverOpen] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    const { user, clinic, logout } = useApp();
    const { t, i18n } = useTranslation(['notification', 'common', 'superadmin']);
    const screens = useBreakpoint();

    const isMobile = !screens.md;

    // Fetch notifications on component mount and when popover opens
    useEffect(() => {
        if (notificationPopoverOpen) {
            fetchNotifications();
        }
    }, [notificationPopoverOpen]);

    // Fetch unread count on mount and periodically
    useEffect(() => {
        fetchUnreadCount();
        const interval = setInterval(fetchUnreadCount, 30000); // Refresh every 30 seconds
        return () => clearInterval(interval);
    }, []);

    const fetchUnreadCount = async () => {
        try {
            const response = await notificationService.getUnreadCount();
            if (response.success) {
                setUnreadCount(response.unreadCount || 0);
            }
        } catch (error) {
            console.error('Error fetching unread count:', error);
        }
    };

    const fetchNotifications = async () => {
        setNotificationLoading(true);
        try {
            const response = await notificationService.getAll({
                status: '', // All statuses
                page: 1,
                limit: 5
            });
            if (response.success) {
                const notifData = Array.isArray(response.data) ? response.data : (response.data?.data || []);
                setNotifications(notifData);
            }
        } catch (error) {
            console.error('Error fetching notifications:', error);
        } finally {
            setNotificationLoading(false);
        }
    };

    const handleMarkAsRead = async (id) => {
        try {
            await notificationService.markAsRead(id);
            fetchUnreadCount();
            fetchNotifications();
        } catch (error) {
            console.error('Error marking notification as read:', error);
        }
    };

    const handleDeleteNotification = async (id) => {
        try {
            await notificationService.delete(id);
            fetchNotifications();
            fetchUnreadCount();
        } catch (error) {
            console.error('Error deleting notification:', error);
        }
    };

    // Helper function to translate notification titles that might be translation keys
    const translateTitle = (titleValue) => {
        if (!titleValue) return 'N/A';

        // Check if the title is a translation key (starts with key pattern)
        const keyPattern = /^[a-z]+\.[a-zA-Z]+$/;
        if (keyPattern.test(titleValue)) {
            return t(titleValue);
        }

        // Otherwise, return the title as-is
        return titleValue;
    };

    const notificationContent = (
        <div style={{ width: 350 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <Typography.Text strong>{t("title")}</Typography.Text>
                <Button
                    type="link"
                    size="small"
                    onClick={() => {
                        navigate('/notifications');
                        setNotificationPopoverOpen(false);
                    }}
                >
                    {t("viewAll")}
                </Button>
            </div>
            {notificationLoading ? (
                <Spin style={{ display: 'flex', justifyContent: 'center' }} />
            ) : notifications.length > 0 ? (
                <div style={{ maxHeight: 350, overflowY: 'auto' }}>
                    {notifications.map((notif) => (
                        <div
                            key={notif._id}
                            style={{
                                padding: 10,
                                borderBottom: '1px solid #f0f0f0',
                                cursor: 'pointer',
                                background: notif.isRead ? 'transparent' : '#fafafa',
                                borderLeft: notif.isRead ? 'none' : '3px solid #1890ff'
                            }}
                            onMouseEnter={(e) => (e.currentTarget.style.background = '#f5f5f5')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = notif.isRead ? 'transparent' : '#fafafa')}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                                <div style={{ flex: 1 }}>
                                    <Typography.Text
                                        strong={!notif.isRead}
                                        style={{ fontSize: 13 }}
                                    >
                                        {translateTitle(notif.title)}
                                    </Typography.Text>
                                    <div style={{ fontSize: 12, color: '#666', marginTop: 2 }}>
                                        {notif.patientId?.name} • {notif.notificationType}
                                    </div>
                                </div>
                                <Tag
                                    color={
                                        notif.priority === 'High' ? 'red' :
                                            notif.priority === 'Medium' ? 'orange' : 'green'
                                    }
                                    style={{ fontSize: 10, marginLeft: 8 }}
                                >
                                    {notif.priority}
                                </Tag>
                            </div>
                            <div style={{ fontSize: 11, color: '#999', marginBottom: 6 }}>
                                {dayjs(notif.notificationDate).format('MMM DD, YYYY HH:mm')}
                            </div>
                            <div style={{ display: 'flex', gap: 6 }}>
                                {!notif.isRead && (
                                    <Button
                                        type="link"
                                        size="small"
                                        icon={<CheckOutlined />}
                                        onClick={() => handleMarkAsRead(notif._id)}
                                    >
                                        {t("markRead")}
                                    </Button>
                                )}
                                <Button
                                    type="link"
                                    danger
                                    size="small"
                                    icon={<DeleteOutlined />}
                                    onClick={() => handleDeleteNotification(notif._id)}
                                >
                                    {t("delete")}
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <Empty description={t("noNotifications")} style={{ margin: '20px 0' }} />
            )}
        </div>
    );

    // Auto-collapse on tablet, expand on desktop
    useEffect(() => {
        if (screens.md && !screens.lg) {
            setCollapsed(true);
        } else if (screens.lg) {
            setCollapsed(false);
        }
    }, [screens.md, screens.lg]);

    // Use useMemo to recalculate menu items when language changes
    const menuItems = useMemo(() => {
        const role = user?.role;

        // Base items for all users
        const baseItems = [
            {
                key: '/dashboard',
                icon: <DashboardOutlined />,
                label: t('common:menu.dashboard')
            }
        ];

        // Receptionist/Secretary - Appointments and Patients
        if (role === 'receptionist') {
            return [
                {
                    key: '/appointments',
                    icon: <CalendarOutlined />,
                    label: t('common:menu.appointments')
                },
                {
                    key: '/patients',
                    icon: <TeamOutlined />,
                    label: t('common:menu.patients')
                }
            ];
        }

        // Dentist - Their own patients, appointments, treatments, prescriptions
        if (role === 'dentist') {
            return [
                ...baseItems,
                {
                    key: '/patients',
                    icon: <UserOutlined />,
                    label: t('common:menu.patients')
                },
                {
                    key: '/appointments',
                    icon: <CalendarOutlined />,
                    label: t('common:menu.appointments')
                },
                {
                    key: '/notifications',
                    icon: <BellOutlined />,
                    label: t('common:menu.notifications', 'Notifications')
                },
                {
                    key: '/payments',
                    icon: <DollarOutlined />,
                    label: t('common:menu.payments')
                },
                {
                    key: '/expenses',
                    icon: <BankOutlined />,
                    label: t('common:menu.expenses', 'Expenses')
                },
                {
                    key: '/reports',
                    icon: <BarChartOutlined />,
                    label: t('common:menu.reports')
                }
            ];
        }

        // Admin - Full access
        if (role === 'admin') {
            return [
                ...baseItems,
                {
                    key: '/patients',
                    icon: <UserOutlined />,
                    label: t('common:menu.patients')
                },
                {
                    key: '/dentists',
                    icon: <TeamOutlined />,
                    label: t('common:menu.dentists')
                },
                {
                    key: '/appointments',
                    icon: <CalendarOutlined />,
                    label: t('common:menu.appointments')
                },
                {
                    key: '/treatments',
                    icon: <MedicineBoxOutlined />,
                    label: t('common:menu.treatments')
                },
                {
                    key: '/prescriptions',
                    icon: <FileTextOutlined />,
                    label: t('common:menu.prescriptions')
                },
                {
                    key: '/payments',
                    icon: <DollarOutlined />,
                    label: t('common:menu.payments')
                },
                {
                    key: '/expenses',
                    icon: <BankOutlined />,
                    label: t('common:menu.expenses', 'Expenses')
                },
                {
                    key: '/reports',
                    icon: <BarChartOutlined />,
                    label: t('common:menu.reports')
                },
                {
                    key: '/notifications',
                    icon: <BellOutlined />,
                    label: t('common:menu.notifications', 'Notifications')
                },
                {
                    key: 'settings',
                    icon: <SettingOutlined />,
                    label: t('common:menu.settings'),
                    children: [
                        {
                            key: '/clinic/profile',
                            label: t('common:menu.clinicProfile')
                        },
                        {
                            key: '/clinic/users',
                            label: t('common:menu.users')
                        }
                    ]
                }
            ];
        }

        // Super admin - managed via separate variable below
        if (role === 'super_admin') {
            return baseItems;
        }

        // Pharmacy role - Pharmacy module access
        if (role === 'pharmacy') {
            return [
                {
                    key: '/pharmacy',
                    icon: <DashboardOutlined />,
                    label: t('common:menu.pharmacyDashboard', 'Dashboard'),
                },
                {
                    key: '/pharmacy/inventory',
                    icon: <AppstoreOutlined />,
                    label: t('common:menu.inventory', 'Inventory')
                },
                {
                    key: '/pharmacy/sell',
                    icon: <ShoppingCartOutlined />,
                    label: t('common:menu.sell', 'Sell (POS)')
                },
                {
                    key: '/pharmacy/buy',
                    icon: <ImportOutlined />,
                    label: t('common:menu.buy', 'Buy (Purchase)')
                },
                {
                    key: '/pharmacy/sales',
                    icon: <UnorderedListOutlined />,
                    label: t('common:menu.salesList', 'Sales List')
                },
                {
                    key: '/pharmacy/purchases',
                    icon: <FileTextOutlined />,
                    label: t('common:menu.purchasesList', 'Purchases List')
                },
                {
                    key: '/pharmacy/returns',
                    icon: <RollbackOutlined />,
                    label: t('common:menu.returns', 'Returns')
                },
                {
                    key: '/expenses',
                    icon: <BankOutlined />,
                    label: t('common:menu.expenses', 'Expenses')
                },
                {
                    key: '/pharmacy/reports',
                    icon: <BarChartOutlined />,
                    label: t('common:menu.reports', 'Reports')
                },
            ];
        }

        // Lab Technician role - Laboratory module access
        if (role === 'lab_technician') {
            return [
                {
                    key: '/laboratory',
                    icon: <DashboardOutlined />,
                    label: t('common:menu.labDashboard', 'Dashboard'),
                },
                {
                    key: '/laboratory/orders',
                    icon: <UnorderedListOutlined />,
                    label: t('common:menu.labOrders', 'Orders')
                },
                {
                    key: '/laboratory/reports',
                    icon: <FileTextOutlined />,
                    label: t('common:menu.labReports', 'Reports')
                },
                {
                    key: '/expenses',
                    icon: <BankOutlined />,
                    label: t('common:menu.expenses', 'Expenses')
                }
            ];
        }

        // Radiologist role - Radiology module access
        if (role === 'radiologist') {
            return [
                {
                    key: '/radiology',
                    icon: <DashboardOutlined />,
                    label: t('common:menu.radiologyDashboard', 'Dashboard')
                },
                {
                    key: '/radiology/exams',
                    icon: <CameraOutlined />,
                    label: t('common:menu.radiologyExams', 'Exams')
                },
                {
                    key: '/radiology/reports',
                    icon: <BarChartOutlined />,
                    label: t('common:menu.radiologyReports', 'Reports')
                },
                {
                    key: '/expenses',
                    icon: <BankOutlined />,
                    label: t('common:menu.expenses', 'Expenses')
                }
            ];
        }

        // Default fallback
        return baseItems;
    }, [t, user?.role, i18n.language]);

    // Super admin - has separate simple menu
    const superAdminMenuItems = useMemo(() => [
        {
            key: '/superadmin',
            icon: <BankOutlined />,
            label: t('superadmin:clinicManagement')
        },
        {
            key: '/dashboard',
            icon: <DashboardOutlined />,
            label: t('common:menu.dashboard')
        }
    ], [t, i18n.language]);

    const activeMenuItems = user?.role === 'super_admin' ? superAdminMenuItems : menuItems;

    const userMenu = useMemo(() => (
        <Menu className="user-dropdown-menu" style={{ minWidth: '200px', borderRadius: '8px', padding: '8px' }}>
            <Menu.Item
                key="profile"
                icon={<UserOutlined />}
                onClick={() => navigate('/clinic/profile')}
                style={{ borderRadius: '6px', marginBottom: '4px' }}
            >
                {t('common:menu.profile')}
            </Menu.Item>
            <Menu.Divider style={{ margin: '8px 0' }} />
            <Menu.Item
                key="logout"
                icon={<LogoutOutlined />}
                onClick={logout}
                danger
                style={{ borderRadius: '6px' }}
            >
                {t('common:menu.logout')}
            </Menu.Item>
        </Menu>
    ), [t, navigate, logout, i18n.language]);

    const isRTL = i18n.language === 'ar' || i18n.language === 'ku';

    const siderWidth = 250;
    const collapsedWidth = 80;
    const currentSiderWidth = isMobile ? 0 : (collapsed ? collapsedWidth : siderWidth);

    const handleToggle = () => {
        if (isMobile) {
            setDrawerOpen(!drawerOpen);
        } else {
            setCollapsed(!collapsed);
        }
    };

    const handleMenuClick = ({ key }) => {
        navigate(key);
        if (isMobile) setDrawerOpen(false);
    };

    const siderContent = (
        <>
            <div className="logo-container">
                {clinic?.logo && !(collapsed && !isMobile) ? (
                    <div className="logo-wrapper">
                        <Image
                            src={clinic.logo.startsWith('http') ? clinic.logo : `${process.env.REACT_APP_API_URL}${clinic.logo}`}
                            alt={clinic.clinicName}
                            preview={false}
                            style={{ maxHeight: 45, maxWidth: '100%', borderRadius: '8px' }}
                        />
                    </div>
                ) : (
                    <div className="logo-text">
                        <span className="logo-icon">🦷</span>
                        {(!collapsed || isMobile) && <span className="logo-name">SoftDent</span>}
                    </div>
                )}
            </div>
            <Menu
                theme="dark"
                mode="inline"
                selectedKeys={[location.pathname]}
                items={activeMenuItems}
                onClick={handleMenuClick}
                className="modern-menu"
                style={{ background: 'transparent', border: 'none' }}
            />
        </>
    );

    return (
        <Layout style={{ minHeight: '100vh', background: '#F3EFE3' }}>

            {/* Mobile Drawer */}
            {isMobile && (
                <Drawer
                    placement={isRTL ? 'right' : 'left'}
                    open={drawerOpen}
                    onClose={() => setDrawerOpen(false)}
                    width={siderWidth}
                    bodyStyle={{ padding: 0, background: 'linear-gradient(180deg, #5f7868 0%, #4a5f54 100%)' }}
                    headerStyle={{ display: 'none' }}
                    className="mobile-drawer"
                >
                    {siderContent}
                </Drawer>
            )}

            {/* Desktop/Tablet Sider */}
            {!isMobile && (
                <Sider
                    trigger={null}
                    collapsible
                    collapsed={collapsed}
                    width={siderWidth}
                    collapsedWidth={collapsedWidth}
                    className="modern-sider"
                    style={{
                        overflow: 'auto',
                        height: '100vh',
                        position: 'fixed',
                        [isRTL ? 'right' : 'left']: 0,
                        top: 0,
                        bottom: 0,
                        boxShadow: '2px 0 12px rgba(118, 147, 130, 0.15)',
                        zIndex: 100
                    }}
                >
                    {siderContent}
                </Sider>
            )}

            <Layout style={{
                [isRTL ? 'marginRight' : 'marginLeft']: isMobile ? 0 : currentSiderWidth,
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                background: '#F3EFE3'
            }}>
                <Header className="modern-header" style={{
                    background: '#fff',
                    padding: isMobile ? '0 12px' : '0 32px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    height: '64px',
                    borderRadius: '0 0 16px 16px'
                }}>
                    <div className="header-left">
                        {React.createElement(collapsed || isMobile ? MenuUnfoldOutlined : MenuFoldOutlined, {
                            className: 'trigger-btn',
                            onClick: handleToggle,
                            style: {
                                fontSize: 20,
                                color: '#001529',
                                padding: '8px',
                                borderRadius: '6px',
                                transition: 'all 0.3s'
                            }
                        })}
                    </div>
                    <div className="header-right" style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 20 }}>
                        <LanguageSwitcher />
                        <Popover
                            content={notificationContent}
                            title=""
                            trigger="click"
                            open={notificationPopoverOpen}
                            onOpenChange={setNotificationPopoverOpen}
                            placement="bottomRight"
                            overlayStyle={{ zIndex: 1000 }}
                        >
                            <Badge count={unreadCount} size="small" offset={[-5, 5]}>
                                <BellOutlined
                                    style={{
                                        fontSize: isMobile ? 18 : 20,
                                        color: '#5f7868',
                                        cursor: 'pointer',
                                        padding: '8px',
                                        borderRadius: '6px',
                                        transition: 'all 0.3s',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center'
                                    }}
                                    title="Notifications"
                                    onMouseEnter={(e) => (e.target.style.color = '#2c3e36', e.target.style.background = '#f0f0f0')}
                                    onMouseLeave={(e) => (e.target.style.color = '#5f7868', e.target.style.background = 'transparent')}
                                />
                            </Badge>
                        </Popover>
                        <QuestionCircleOutlined
                            onClick={() => setGuideOpen(true)}
                            style={{
                                fontSize: isMobile ? 18 : 20,
                                color: '#5f7868',
                                cursor: 'pointer',
                                padding: '8px',
                                borderRadius: '6px',
                                transition: 'all 0.3s',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                            title={t('common:menu.help', 'System Guide')}
                            onMouseEnter={(e) => (e.target.style.color = '#2c3e36', e.target.style.background = '#f0f0f0')}
                            onMouseLeave={(e) => (e.target.style.color = '#5f7868', e.target.style.background = 'transparent')}
                        />
                        {!isMobile && (
                            <div className="user-info" style={{
                                textAlign: isRTL ? 'left' : 'right',
                                marginRight: isRTL ? '0' : '8px',
                                marginLeft: isRTL ? '8px' : '0'
                            }}>
                                <Text strong style={{ fontSize: '14px', color: '#2c3e36' }}>{user?.name}</Text>
                                <br />
                                <Text type="secondary" style={{ fontSize: '12px', color: '#5f7868' }}>
                                    {user?.role?.toUpperCase()} • {clinic?.clinicName}
                                </Text>
                            </div>
                        )}
                        <Dropdown overlay={userMenu} placement={isRTL ? 'bottomLeft' : 'bottomRight'}>
                            <Avatar
                                size={isMobile ? 36 : 44}
                                icon={<UserOutlined />}
                                className="user-avatar"
                            />
                        </Dropdown>
                    </div>
                </Header>
                <Content style={{ margin: isMobile ? '12px 8px' : '24px', overflow: 'initial' }}>
                    <div style={{
                        padding: isMobile ? '16px' : '32px',
                        background: '#fff',
                        minHeight: 'calc(100vh - 88px)',
                        borderRadius: '12px',
                        boxShadow: '0 4px 16px rgba(118, 147, 130, 0.1)'
                    }}>
                        {children}
                    </div>
                </Content>
            </Layout>

            <SystemGuideModal isOpen={guideOpen} onClose={() => setGuideOpen(false)} />
        </Layout>
    );
};

export default MainLayout;
